import { Button, Box } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import StatusChip from "../../../../components/StatusChip";
import PaymentDrawer from "../../../../components/app/provider/billing/payment/PaymentDrawer";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import useDisclosure from "../../../../hooks/use-disclosure";
import { usePaymentsDue } from "../../../../queries/patient-portal/dashboard/use-payments-due";
import { getStripePromise } from "../../../../queries/stripe/use-stripe";
import {
  formatDate,
  formatDateTime,
} from "../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  getFormattedId,
} from "../../../../utils/utils";

type StatusVariant =
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending";

interface PaymentDueRow {
  dueDate?: string;
  superBillUuid?: string;
  appointmentUuid?: string;
  renderingProviderName?: string;
  encounterDate?: string;
  appointmentDate?: string;
  dateOfService?: string;
  billDate?: string;
  amountDue?: number;
  subTotal?: number;
  patientBalance?: number;
  superBillStatus?: string;
  appointmentType?: string;
  chiefComplaint?: string;
  placeOfService?: string;
}

const STATUS_VARIANT_MAP: Record<string, StatusVariant> = {
  PENDING: "pending",
  PARTIALLY_PAID: "info",
  PAID: "success",
  OVERDUE: "error",
};

const STATUS_LABEL_MAP: Record<string, string> = {
  PENDING: "Pending",
  PARTIALLY_PAID: "Partial",
  PAID: "Paid",
  OVERDUE: "Overdue",
};

const resolveStatus = (row: PaymentDueRow): string => {
  const status = row?.superBillStatus;
  if (
    status === "PENDING" &&
    row?.billDate &&
    dayjs(row.billDate).isBefore(dayjs(), "day")
  ) {
    return "OVERDUE";
  }
  return status ?? "PENDING";
};

const formatAmount = (value?: number) =>
  value != null ? `$${Number(value).toFixed(2)}` : "-";

const PayementsDue = () => {
  const outletContext = useOutletContext<{ patientId?: string } | undefined>();
  const [patient] = useCurrentUser();
  const patientUuid = outletContext?.patientId ?? patient?.uuid ?? "";

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: paymentsDueData, isFetching } = usePaymentsDue({
    patientUuid,
    pagination,
  });

  const rows: PaymentDueRow[] =
    (paymentsDueData?.data?.content as PaymentDueRow[]) ?? [];
  const rowCount = paymentsDueData?.data?.page?.totalElements ?? 0;

  const client = useQueryClient();
  const payDisc = useDisclosure(false);

  const handlePayNow = (row: PaymentDueRow) => {
    payDisc.open({
      uuid: null,
      patientId: patientUuid,
      superBillId: row?.superBillUuid,
      amount: row?.amountDue ?? row?.patientBalance,
    });
  };

  const columns = applyDefaultColumnSettings([
    {
      field: "serviceVisitType",
      headerName: "Service/ Visit Type",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) =>
        row?.appointmentType ||
        row?.chiefComplaint ||
        row?.placeOfService ||
        "-",
    },
    {
      field: "provider",
      headerName: "Provider",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) =>
        row?.renderingProviderName ?? "-",
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) =>
        row?.superBillUuid ? getFormattedId(row.superBillUuid) : "-",
    },
    {
      field: "serviceDate",
      headerName: "Service Date",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) => {
        const date =
          row?.encounterDate || row?.appointmentDate || row?.dateOfService;
        return date ? formatDateTime(date) : "-";
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) =>
        formatAmount(row?.amountDue ?? row?.subTotal),
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) =>
        row?.dueDate ? formatDate(row.dueDate) : "-",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) => {
        const status = resolveStatus(row);
        return (
          <StatusChip
            label={STATUS_LABEL_MAP[status] ?? status}
            variant={STATUS_VARIANT_MAP[status] ?? "default"}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }: { row: PaymentDueRow }) => (
        <Button
          variant="text"
          size="small"
          sx={{ textTransform: "none", textDecoration: "underline" }}
          onClick={() => handlePayNow(row)}
        >
          Pay Now
        </Button>
      ),
    },
  ]);

  return (
    <Box width="100%" height="100%">
      <CustomDataGridV2
        getRowId={(row) => row?.superBillUuid}
        rows={rows}
        rowCount={rowCount}
        columns={columns}
        loading={isFetching}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
      />

      <Elements stripe={getStripePromise()}>
        <PaymentDrawer
          disclosureHost={payDisc}
          amount={payDisc?.data?.amount}
          shouldPassPatientId
          onSuccess={() => {
            client.invalidateQueries({ queryKey: ["payments-due"] });
          }}
        />
      </Elements>
    </Box>
  );
};

export default PayementsDue;
